/**
 * Domains API Client
 *
 * Endpoints for managing domains (subdomains and custom domains).
 * All requests go through the centralized API SDK for consistent
 * authentication, token refresh, and 401 handling.
 *
 * Error Handling:
 * All methods throw ApiClientError on failure. Use try/catch blocks
 * or .catch() to handle errors. The ApiClientError provides:
 * - Structured error information (code, type, message)
 * - Validation violations for form errors
 * - Type-checking methods (isValidationError(), isNotFound(), etc.)
 */

import { api } from './sdk';
import { buildListQuery, type Paginated, type PaginationParams } from './pagination';

// Re-export error handling utilities for consumers
export { ApiClientError, isApiClientError } from './sdk';

export interface Domain {
  id: string;
  siteId: string | null;  // null for unattached domains
  siteName?: string | null;
  type: 'subdomain' | 'custom';
  domain: string;
  isPrimary: boolean;
  verified: boolean;
  routingConfigured: boolean;
  sslStatus: 'none' | 'pending' | 'active' | 'failed';
  // Approximated fields for apex domain support
  apxVirtualHostId?: number | null;
  apxStatus?: 'pending_dns' | 'pending_ssl' | 'active' | 'error' | null;
  apxSslActive?: boolean;
  apxLastCheckedAt?: string | null;
  attachedAt?: string;
  detachedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface DnsRecord {
  type: 'CNAME' | 'A' | 'TXT';
  name: string;
  value: string;
  ttl?: number;
}

// DNS instruction for a single step
export interface DnsInstruction {
  step?: number;
  purpose: string;
  record?: DnsRecord;
  records?: DnsRecord[];  // For A records (apex + www)
  note: string;
  clusterIp?: string;  // Approximated cluster IP for A records
}

// Full DNS instructions for two-step verification
export interface DnsInstructions {
  verification?: DnsInstruction;  // Step 1: TXT record for ownership
  routing?: DnsInstruction;       // Step 2: A records for traffic routing (Approximated)
}

// A record routing instructions from Approximated
export interface ARecordInstructions {
  purpose: string;
  records: DnsRecord[];
  clusterIp: string;
  note: string;
}

// New two-step verification response
export interface DomainVerification {
  ownership: {
    verified: boolean;
    record: DnsRecord;
    error: string | null;
  };
  routing?: {
    configured: boolean;
    currentValue: string | null;
    expectedValue: string;
    records: DnsRecord[];  // A records for Approximated
    error: string | null;
  };
  ssl?: {
    active: boolean;
    status: 'pending_dns' | 'provisioning' | 'active';
  };
  apxStatus?: string;
  fullyVerified: boolean;  // Both ownership, routing, and SSL verified
  canAttach: boolean;       // Ownership verified, can attach to site
  lastChecked: string;
}

// Legacy format (for backwards compatibility)
export interface LegacyDomainVerification {
  verified: boolean;
  dnsRecords: DnsRecord[];
  lastChecked: string;
  error?: string;
}

export interface CreateDomainRequest {
  siteId?: string;  // Optional - domains can be created without being attached to a site
  domain: string;
}

export interface CreateDomainResponse {
  domain: Domain;
  dnsInstructions: DnsInstructions;
}

// SDK unwraps collection responses, so we receive array directly
type AvailableDomain = { id: string; domain: string; verified: boolean; createdAt: string };

interface DetachDomainResponse {
  id: string;
  domain: string;
  previousSiteId: string;
}

interface AttachDomainResponse extends Domain {
  dnsInstructions: ARecordInstructions;
}

interface ForceCheckResponse {
  domain: string;
  apxStatus: string;
  dnsConfigured: boolean;
  sslActive: boolean;
  lastChecked: string;
}

interface DnsInstructionsResponse {
  domain: string;
  verified: boolean;
  siteId: string | null;
  ownership: DnsInstruction;
  routing: ARecordInstructions | null;
}

export const domainsApi = {
  /**
   * Get all domains for the organization (paginated).
   * @throws {ApiClientError} On API failure
   */
  getDomains: (params: PaginationParams = {}): Promise<Paginated<Domain>> =>
    api.get<Paginated<Domain>>(`/api/domains${buildListQuery(params)}`),

  /**
   * Get a specific domain
   * @throws {ApiClientError} On API failure (404 if not found)
   */
  getDomain: (domainId: string): Promise<Domain> =>
    api.get<Domain>(`/api/domains/${encodeURIComponent(domainId)}`),

  /**
   * Add a custom domain to a site
   * @throws {ApiClientError} On API failure (validation errors, conflicts, etc.)
   */
  createDomain: (request: CreateDomainRequest): Promise<CreateDomainResponse> =>
    api.post<CreateDomainResponse>('/api/domains', request),

  /**
   * Verify domain DNS configuration
   * @throws {ApiClientError} On API failure (404 if domain not found)
   */
  verifyDomain: (domainId: string): Promise<DomainVerification> =>
    api.get<DomainVerification>(`/api/domains/${encodeURIComponent(domainId)}/verify`),

  /**
   * Delete a custom domain
   * @throws {ApiClientError} On API failure (404 if not found, 403 if forbidden)
   */
  deleteDomain: (domainId: string): Promise<void> =>
    api.delete<void>(`/api/domains/${encodeURIComponent(domainId)}`),

  /**
   * Set a domain as primary for its site
   * @throws {ApiClientError} On API failure (404 if not found, 400 if invalid)
   */
  setPrimaryDomain: (domainId: string): Promise<Domain> =>
    api.post<Domain>(`/api/domains/${encodeURIComponent(domainId)}/set-primary`),

  /**
   * Get available domains for attachment (verified, not attached).
   * Paginated; pass `search` to filter by domain substring.
   * @throws {ApiClientError} On API failure
   */
  getAvailableDomains: (
    params: PaginationParams & { search?: string } = {},
  ): Promise<Paginated<AvailableDomain>> => {
    const { page, perPage, search } = params;
    const url = `/api/domains/available${buildListQuery({ page, perPage }, { search })}`;
    return api.get<Paginated<AvailableDomain>>(url);
  },

  /**
   * Attach a domain to a site (creates Approximated virtual host)
   * @throws {ApiClientError} On API failure (404 if not found, 400 if invalid)
   */
  attachDomain: (domainId: string, siteId: string): Promise<AttachDomainResponse> =>
    api.post<AttachDomainResponse>(`/api/domains/${encodeURIComponent(domainId)}/attach`, { siteId }),

  /**
   * Detach a domain from its site (deletes Approximated virtual host)
   * @throws {ApiClientError} On API failure (404 if not found, 400 if not attached)
   */
  detachDomain: (domainId: string): Promise<DetachDomainResponse> =>
    api.post<DetachDomainResponse>(`/api/domains/${encodeURIComponent(domainId)}/detach`),

  /**
   * Force refresh Approximated status for a domain
   * @throws {ApiClientError} On API failure (404 if not found)
   */
  forceCheckDomain: (domainId: string): Promise<ForceCheckResponse> =>
    api.post<ForceCheckResponse>(`/api/domains/${encodeURIComponent(domainId)}/force-check`),

  /**
   * Get DNS instructions for a domain
   * @throws {ApiClientError} On API failure (404 if not found)
   */
  getDnsInstructions: (domainId: string): Promise<DnsInstructionsResponse> =>
    api.get<DnsInstructionsResponse>(`/api/domains/${encodeURIComponent(domainId)}/dns-instructions`),
};
